import { useEffect, useMemo, useRef, useState } from "react";
import { AddressableGroupMode, IAddressableGroupDetail, IConfig, IConfigWorkspace, IOrganization, IWorkspaceAddressableParam, ProductType } from "./AddressableSetup.types";
import { Alert, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel, Radio, RadioGroup, Step, StepLabel, Stepper, Switch, Table, TableCell, TableContainer, TableRow, Typography, IconButton} from "@mui/material";
import { maskCNPJ } from "./AddressableSetup.utils";
import { NO_DEPENDENTS_SENTINEL, NENHUM_LABEL } from "./AddressableSetup.constants";
import { DEPENDENT_FAMILY_OPTIONS, toCanonicalDependentTypes } from "./AddressableSetup.dependentFamilies";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import api from "services/api";
import { ErrorOutline, InfoOutlined } from "@mui/icons-material";
import { RadioCard } from "./AddressableSetup.styled";
import { useAuth } from "contexts/auth";
import styled from "styled-components";
import { ArrowBack } from "@mui/icons-material";

const REGIOES = [
  "Acre",
  "Alagoas",
  "Amapá",
  "Amazonas",
  "Bahia",
  "Ceará",
  "Distrito Federal",
  "Espírito Santo",
  "Goiás",
  "Maranhão",
  "Mato Grosso",
  "Mato Grosso do Sul",
  "Minas Gerais",
  "Pará",
  "Paraíba",
  "Paraná",
  "Pernambuco",
  "Piauí",
  "Rio de Janeiro",
  "Rio Grande do Norte",
  "Rio Grande do Sul",
  "Rondônia",
  "Roraima",
  "Santa Catarina",
  "São Paulo",
  "Sergipe",
  "Tocantins"
];


interface IProps {
  organization: IOrganization;
  addressableGroup: IWorkspaceAddressableParam;
  handleRemoveGroup: () => void;
  refetchOrganizations: () => Promise<any>;
  updateSelectedOrganization: (organizationId: string, updatedData?: IOrganization[]) => void;
  mode: AddressableGroupMode;
}

type ConfigType = "CARGO" | "REGIAO" | "LOTACAO";

export const PageTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: #191c1a;
`;

export const PageSubtitle = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: #0F0F1E99;
`;

export function AddressableSetupCreate({ addressableGroup, organization, handleRemoveGroup, refetchOrganizations, updateSelectedOrganization, mode }: IProps) {
  const queryClient = useQueryClient();
  const user = useAuth();
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<IConfigWorkspace[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [configType, setConfigType] = useState<ConfigType>("CARGO");
  const [cargoWorkspaceMap, setCargoWorkspaceMap] = useState<Record<string, Record<string, boolean>>>({});
  const [regiaoWorkspaceMap, setRegiaoWorkspaceMap] = useState<Record<string, Record<string, boolean>>>({});
  const [lotacaoWorkspaceMap, setLotacaoWorkspaceMap] = useState<Record<string, Record<string, boolean>>>({});
  const [dependentTypeWorkspaceMap, setDependentTypeWorkspaceMap] = useState<Record<string, string[]>>({});
  const [allowDepOverrideMap, setAllowDepOverrideMap] = useState<Record<string, boolean>>({});

  const roleWorkspaceMap = configType === "CARGO" ? cargoWorkspaceMap : configType === "REGIAO" ? regiaoWorkspaceMap : lotacaoWorkspaceMap;
  const setRoleWorkspaceMap = configType === "CARGO" ? setCargoWorkspaceMap : configType === "REGIAO" ? setRegiaoWorkspaceMap : setLotacaoWorkspaceMap;
  const hasPrePopulatedWorkspaces = useRef(false);
  const hasPrePopulatedAssignments = useRef(false);
  const hasPrePopulatedDependents = useRef(false);

  const isEditMode = mode === "edit";
  const alreadyConfiguredWorkspaces = isEditMode ? [] : addressableGroup.workspaces.map(workspace => workspace.id);

  // Funções helper para ordenação alfabética
  const getSortedRoles = (): string[] => {
    if (!config?.roles) return [];
    return [...config.roles].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  };

  const getSortedRegions = (): string[] => {
    return [...REGIOES].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  };

  const getSortedLotacoes = (): string[] => {
    if (!config?.lotacoes) return [];
    return [...config.lotacoes].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  };

  const getSortedItems = (): string[] => {
    if (configType === "CARGO") return getSortedRoles();
    if (configType === "REGIAO") return getSortedRegions();
    return getSortedLotacoes();
  };

  const hasLivreAdesao = (): boolean => {
    return selectedWorkspaces.some(ws => ws.contractType === "LIVRE_ADESAO");
  };

  const handleToggleRoleWorkspace = (role: string, workspaceId: string) => {
    setRoleWorkspaceMap(prev => {
      const newMap = { ...prev };
      if (!newMap[role]) {
        newMap[role] = {};
      }

      const currentValue = newMap[role]?.[workspaceId] || false;

      // Se está tentando ativar e todos são compulsórios
      if (!currentValue && !hasLivreAdesao()) {
        // Desativa todos os outros workspaces para este cargo
        newMap[role] = {};
      }

      newMap[role] = {
        ...newMap[role],
        [workspaceId]: !currentValue
      };

      return newMap;
    });
  };

  const isRoleWorkspaceSelected = (role: string, workspaceId: string): boolean => {
    return roleWorkspaceMap[role]?.[workspaceId] || false;
  };

  // Verifica se todos os itens (cargos/regiões) de um workspace estão selecionados
  const areAllItemsSelectedForWorkspace = (workspaceId: string): boolean => {
    const items = getSortedItems();
    if (items.length === 0) return false;

    return items.every(item => {
      return roleWorkspaceMap[item]?.[workspaceId] === true;
    });
  };

  // Seleciona ou deseleciona todos os itens (cargos/regiões) para um workspace
  const handleToggleAllItemsForWorkspace = (workspaceId: string) => {
    const items = getSortedItems();
    const allSelected = areAllItemsSelectedForWorkspace(workspaceId);

    setRoleWorkspaceMap(prev => {
      const newMap = { ...prev };

      items.forEach(item => {
        if (!newMap[item]) {
          newMap[item] = {};
        }

        // Se não tem livre adesão e está tentando selecionar
        if (!allSelected && !hasLivreAdesao()) {
          // Desativa todos os outros workspaces para este item
          newMap[item] = { [workspaceId]: true };
        } else {
          // Caso contrário, apenas toggle o valor
          newMap[item] = {
            ...newMap[item],
            [workspaceId]: !allSelected
          };
        }
      });

      return newMap;
    });
  };

  const areAllItemsAssigned = (): boolean => {
    const items = getSortedItems();

    return items.every((item) => {
      const itemWorkspaces = roleWorkspaceMap[item];
      if (!itemWorkspaces) return false;

      // Verifica se pelo menos um workspace está selecionado para este item
      return Object.values(itemWorkspaces).some(isSelected => isSelected === true);
    });
  };

  // Com apenas um workspace, endereçabilidade funciona como filtro:
  // basta selecionar pelo menos um item para prosseguir; itens não marcados
  // ficam de fora do grupo.
  const hasAtLeastOneAssignment = (): boolean => {
    return Object.values(roleWorkspaceMap).some(itemWorkspaces =>
      Object.values(itemWorkspaces).some(isSelected => isSelected === true)
    );
  };

  const canProceedFromAssignments = (): boolean => {
    return selectedWorkspaces.length === 1
      ? hasAtLeastOneAssignment()
      : areAllItemsAssigned();
  };

  const handleToggleDependentType = (workspaceId: string, type: string) => {
    setDependentTypeWorkspaceMap(prev => {
      const current = (prev[workspaceId] || []).filter(
        t => t !== NO_DEPENDENTS_SENTINEL,
      );
      const isAdding = !current.includes(type);
      const next = isAdding
        ? [...current, type]
        : current.filter(t => t !== type);

      return {
        ...prev,
        [workspaceId]: next.length === 0 ? [NO_DEPENDENTS_SENTINEL] : next,
      };
    });
  };

  const isDependentTypeSelected = (workspaceId: string, type: string): boolean => {
    const types = dependentTypeWorkspaceMap[workspaceId] || [];
    return types.includes(type);
  };

  const isNoneSelected = (workspaceId: string): boolean => {
    const arr = dependentTypeWorkspaceMap[workspaceId];
    return !arr || arr.length === 0 || arr.includes(NO_DEPENDENTS_SENTINEL);
  };

  const setNenhumForWorkspace = (workspaceId: string) => {
    setDependentTypeWorkspaceMap(prev => ({
      ...prev,
      [workspaceId]: [NO_DEPENDENTS_SENTINEL],
    }));
  };

  const isWorkspaceSelected = (workspaceId: string) => {
    return selectedWorkspaces.some(ws => ws.id === workspaceId);
  };

  const getSelectedProductType = (): ProductType | null => {
    if (selectedWorkspaces.length === 0) return null;
    return selectedWorkspaces[0].product.productType;
  };

  const isWorkspaceDisabled = (workspace: IConfigWorkspace): boolean => {
    const selectedProductType = getSelectedProductType();
    if (selectedProductType === null) return false;
    return workspace.product.productType !== selectedProductType;
  };

  const handleToggleWorkspace = (workspace: IConfigWorkspace) => {
    if (isWorkspaceDisabled(workspace)) return;

    setSelectedWorkspaces(prev => {
      const isAlreadySelected = prev.some(ws => ws.id === workspace.id);

      if (isAlreadySelected) {
        return prev.filter(ws => ws.id !== workspace.id);
      } else {
        return [...prev, workspace];
      }
    });
  };

  const productTypeLabels: Record<ProductType, string> = {
    HEALTH: "Grupo de Saúde",
    DENTAL: "Grupo de Dental",
    LIFE: "Grupo de Vida",
  };

  const contractTypeLabels = {
    LIVRE_ADESAO: "Livre Adesão",
    COMPULSORIO: "Compulsório",
  }

  const formatWorkspaceLabel = (workspace: IConfigWorkspace): string => {
    const prefix = workspace.id.split("-")[0].toUpperCase();
    const supplier = workspace.product.supplierOrganization.corporateName;
    const name = workspace.product.name;
    const contract = contractTypeLabels[workspace.contractType];
    if (supplier) {
      return `${prefix} - ${supplier} - ${name} - ${contract}`;
    }
    return `${prefix} - ${name} - ${contract}`;
  };

  const formatWorkspaceColumnLabel = (workspace: IConfigWorkspace): string => {
    const prefix = workspace.id.split("-")[0].toUpperCase();
    const supplier = workspace.product.supplierOrganization.corporateName;
    const name = workspace.product.name;
    if (supplier) {
      return `${prefix} - ${supplier} - ${name}`;
    }
    return `${prefix} - ${name}`;
  };

  function handleNext() {
    setActiveStep(prev => prev + 1);
  }

  function handleBack() {
    setActiveStep(prev => prev - 1);
  }

  async function handleCreate() {
    try {

      const addressableType = configType === "CARGO" ? "ROLE" : configType === "REGIAO" ? "REGION" : "LOTACAO";

      const addressabilityRoleParams: Array<{ role: string; workspaceId: string }> = [];
      const addressabilityRegionParams: Array<{ region: string; workspaceId: string }> = [];
      const addressabilityLotacaoParams: Array<{ lotacao: string; workspaceId: string }> = [];

      // Itera sobre o roleWorkspaceMap para construir os arrays
      Object.entries(roleWorkspaceMap).forEach(([item, workspaces]) => {
        Object.entries(workspaces).forEach(([workspaceId, isSelected]) => {
          if (isSelected) {
            if (configType === "CARGO") {
              addressabilityRoleParams.push({
                role: item.toUpperCase(),
                workspaceId
              });
            } else if (configType === "REGIAO") {
              addressabilityRegionParams.push({
                region: item.toUpperCase(),
                workspaceId
              });
            } else {
              addressabilityLotacaoParams.push({
                lotacao: item.toUpperCase(),
                workspaceId
              });
            }
          }
        });
      });

      const workspaceIds = selectedWorkspaces.map(ws => ws.id);

      // Constrói addressabilityDependentTypeParams — uma entrada por workspace
      // selecionado. A entrada existe mesmo quando só o flag de override está
      // ligado e nenhum tipo de dependente foi escolhido (necessário para
      // persistir o flag em workspace_rule.rulesAddressability). Quando nenhum
      // tipo é marcado, o array vai como [NO_DEPENDENTS_SENTINEL] ("Nenhum"),
      // que o backend interpreta como "nenhum dependente permitido".
      const addressabilityDependentTypeParams = selectedWorkspaces.map((ws) => {
        const arr = dependentTypeWorkspaceMap[ws.id];
        const allowedDependentTypes =
          !arr || arr.length === 0 ? [NO_DEPENDENTS_SENTINEL] : arr;
        return {
          workspaceId: ws.id,
          allowedDependentTypes,
          allowDependentDecisionOverride: allowDepOverrideMap[ws.id] ?? false,
        };
      });

      const payload = {
        addressableType,
        addressabilityRoleParams,
        addressabilityRegionParams,
        addressabilityLotacaoParams,
        addressabilityDependentTypeParams,
        workspaceIds
      };

      // Chamada à API
      if (isEditMode) {
        await api.put(`/v2/addressable/groups/${addressableGroup.id}`, payload);
      } else {
        await api.post(`/v2/addressable/${organization.id}`, payload);
      }

      // Invalida as queries para recarregar os dados atualizados sem cache
      const federalTaxPayerId = user.user?.organization?.cnpj;
      const invalidations = [
        queryClient.invalidateQueries({ queryKey: ["addressable-setup-clients", federalTaxPayerId] }),
        queryClient.invalidateQueries({ queryKey: ["create-addressable-group", organization.id] }),
      ];
      if (isEditMode) {
        invalidations.push(
          queryClient.invalidateQueries({ queryKey: ["addressable-group-detail", addressableGroup.id] })
        );
      }
      await Promise.all(invalidations);

      // Recarrega os dados da lista de organizações e pega o resultado atualizado
      const refetchResult = await refetchOrganizations();

      // O refetch retorna os dados já processados pela função select da query
      const updatedOrganizations = refetchResult?.data as IOrganization[] | undefined;

      // Atualiza a organização selecionada com os dados mais recentes
      if (updatedOrganizations) {
        updateSelectedOrganization(organization.id, updatedOrganizations);
      }

      // Volta para a tela de listagem
      handleRemoveGroup();
    } catch(err) {
      console.error(err);
    }
  }
  
  const configUrl = isEditMode
    ? `/v2/addressable/fetch-addressable-workspaces/${organization.id}?editingGroupId=${addressableGroup.id}`
    : `/v2/addressable/fetch-addressable-workspaces/${organization.id}`;

  const [configResult, groupDetailResult] = useQueries({
    queries: [
      {
        queryKey: ["create-addressable-group", organization.id, isEditMode ? addressableGroup.id : null],
        queryFn: () => api.get<{ data: IConfig }>(configUrl),
        select: (data: any) => data?.data?.data as IConfig,
      },
      {
        queryKey: ["addressable-group-detail", addressableGroup.id],
        queryFn: () => api.get<{ data: IAddressableGroupDetail }>(`/v2/addressable/groups/${addressableGroup.id}`),
        select: (data: any) => data?.data?.data as IAddressableGroupDetail,
        enabled: isEditMode,
        retry: false,
      },
    ],
  });

  const isLoading = !configResult.isFetched && configResult.isFetching;
  const isError = !configResult.isFetched && configResult.isError;

  const config = configResult.data as IConfig | undefined;
  const groupDetail = groupDetailResult.data as IAddressableGroupDetail | undefined;

  // In edit mode, ensure the editing group's workspaces are available in the config list.
  // The config endpoint only returns unconfigured workspaces, so the editing group's
  // workspaces (already configured) may be missing. We merge them in from addressableGroup data.
  const editWorkspaces = useMemo(() => {
    if (!isEditMode || !config) return [];
    const configIds = new Set(config.workspaces.map(ws => ws.id));
    return addressableGroup.workspaces
      .filter(ws => !configIds.has(ws.id))
      .map((ws): IConfigWorkspace => ({
        id: ws.id,
        contractType: "COMPULSORIO",
        product: {
          id: ws.product.id,
          productType: ws.product.productType,
          name: ws.product.productType,
          supplierOrganization: { corporateName: "" },
        },
      }));
  }, [isEditMode, config, addressableGroup.workspaces]);

  const allWorkspaces = useMemo(() => {
    if (!config) return [];
    return [...config.workspaces, ...editWorkspaces];
  }, [config, editWorkspaces]);

  // Phase 1: Pre-select workspaces using data we already have from the parent
  useEffect(() => {
    if (!isEditMode || !config || hasPrePopulatedWorkspaces.current) return;
    hasPrePopulatedWorkspaces.current = true;

    const editingWorkspaceIds = new Set(addressableGroup.workspaces.map(ws => ws.id));
    const preSelectedWorkspaces = allWorkspaces.filter(ws =>
      editingWorkspaceIds.has(ws.id)
    );
    setSelectedWorkspaces(preSelectedWorkspaces);
  }, [isEditMode, config, allWorkspaces]);

  // Phase 2: Pre-populate role/region assignments when group detail loads
  useEffect(() => {
    if (!isEditMode || !config || !groupDetail || hasPrePopulatedAssignments.current) return;
    hasPrePopulatedAssignments.current = true;

    // Set config type from group detail
    const typeMap: Record<string, ConfigType> = { "ROLE": "CARGO", "REGION": "REGIAO", "LOTACAO": "LOTACAO" };
    setConfigType(typeMap[groupDetail.addressableType] || "CARGO");

    // Build roleWorkspaceMap from params
    const newMap: Record<string, Record<string, boolean>> = {};
    const params = groupDetail.addressableType === "ROLE"
      ? groupDetail.addressabilityRoleParams
      : groupDetail.addressableType === "REGION"
        ? groupDetail.addressabilityRegionParams
        : (groupDetail.addressabilityLotacaoParams || []);

    const itemKey = groupDetail.addressableType === "ROLE" ? "role" : groupDetail.addressableType === "REGION" ? "region" : "lotacao";
    const referenceItems = groupDetail.addressableType === "ROLE"
      ? (config.roles || [])
      : groupDetail.addressableType === "REGION"
        ? REGIOES
        : (config.lotacoes || []);

    params.forEach((param: any) => {
      const apiName: string = param[itemKey];
      // Case-insensitive match against reference items
      const matchedItem = referenceItems.find(
        (item: string) => item.toUpperCase() === apiName.toUpperCase()
      );
      const displayName = matchedItem || apiName;

      if (!newMap[displayName]) {
        newMap[displayName] = {};
      }
      newMap[displayName][param.workspaceId] = true;
    });

    if (groupDetail.addressableType === "ROLE") {
      setCargoWorkspaceMap(newMap);
    } else if (groupDetail.addressableType === "REGION") {
      setRegiaoWorkspaceMap(newMap);
    } else {
      setLotacaoWorkspaceMap(newMap);
    }
  }, [isEditMode, config, groupDetail]);

  // Phase 3: Pre-populate dependent type assignments when group detail loads
  useEffect(() => {
    if (!isEditMode || !groupDetail || hasPrePopulatedDependents.current) return;
    hasPrePopulatedDependents.current = true;

    // Build dependentTypeWorkspaceMap from params
    const newMap: Record<string, string[]> = {};

    const newOverrideMap: Record<string, boolean> = {};
    if (groupDetail.addressabilityDependentTypeParams) {
      groupDetail.addressabilityDependentTypeParams.forEach((param) => {
        // Listas antigas podem trazer "01 - Cônjuge" ou a grafia do eSocial; a tela
        // mostra e regrava pela família (AddressableSetup.dependentFamilies.ts).
        newMap[param.workspaceId] = toCanonicalDependentTypes(param.allowedDependentTypes);
        if (param.allowDependentDecisionOverride) {
          newOverrideMap[param.workspaceId] = true;
        }
      });
    }

    setDependentTypeWorkspaceMap(newMap);
    setAllowDepOverrideMap(newOverrideMap);
  }, [isEditMode, groupDetail]);

  return (
    <>
      {isLoading && (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" gap={3}>
          <CircularProgress size={20} thickness={4} />
        </Box>
      )}
      {isError && (
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" gap={3} textAlign="center">
          <ErrorOutline sx={{ fontSize: 40, color: 'error.main' }} />
          <Typography variant="h6" color="textPrimary">Ops! Algo deu errado</Typography>
          <Typography variant="body1" color="textSecondary" maxWidth={400}>Não foi possível carregar a listagem de workspaces. Por favor, tente novamente.</Typography>
        </Box>
      )}
      {config && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>

            <PageTitle>
            <IconButton
              size="small"
              onClick={handleRemoveGroup}
            >
              <ArrowBack color="primary" />
            </IconButton>
              {isEditMode ? "Editar grupo" : "Novo grupo"}</PageTitle>
            <PageSubtitle>
              {isEditMode
                ? "Visualize e edite a configuração do grupo de endereçabilidade"
                : "Primeiro você precisa criar o nome para o grupo e selecionar os workspaces"}
            </PageSubtitle>
          </div>
          <Stepper
            activeStep={activeStep}
            sx={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row', alignItems: 'center', px: 2 }}
          >
            {["Selecionar workspaces", "Definir titulares", "Definir dependentes"].map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'row', justifyContent: "space-between" }}>
            <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>Cliente</p>
              <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{organization.corporateName}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>CNPJ</p>
              <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{maskCNPJ(organization.federalTaxId)}</span>
            </div>
            {!!selectedWorkspaces.length && (
              <div style={{ display: 'flex', flexDirection: "column", width: "300px" }}>
                <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>Nome do grupo</p>
                <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{productTypeLabels[selectedWorkspaces[0].product.productType]}</span>
              </div>
            )}
          </div>
          <Alert
            style={{ backgroundColor: "#E5F6FD", display: "flex", alignItems: "center", borderRadius: "8px" }} 
            icon={<InfoOutlined style={{ color: "#014361"}}/>}
          >
            <Typography fontWeight={600} color="#014361">
              {isEditMode
                ? "Lembrete: Workspaces já associados a outro grupo não aparecerão nesta lista. Os workspaces deste grupo são exibidos."
                : "Lembrete: Workspaces já associados a outro grupo não aparecerão nesta lista."}
            </Typography>
          </Alert>
          {activeStep === 0 && (
            <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
              <Table>
                {allWorkspaces.filter(workspace => !alreadyConfiguredWorkspaces.includes(workspace.id)).map(workspace => {
                  const disabled = isEditMode || isWorkspaceDisabled(workspace);
                  return (
                    <TableRow
                      key={workspace.id}
                      style={{
                        cursor: disabled ? "default" : "pointer",
                        opacity: isEditMode ? 0.6 : (isWorkspaceDisabled(workspace) ? 0.5 : 1)
                      }}
                      onClick={isEditMode ? undefined : () => handleToggleWorkspace(workspace)}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isWorkspaceSelected(workspace.id)}
                          disabled={disabled}
                          onChange={isEditMode ? undefined : () => handleToggleWorkspace(workspace)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>{formatWorkspaceLabel(workspace)}</TableCell>
                    </TableRow>
                  );
                })}
              </Table>
            </TableContainer>
          )}
          {activeStep === 1 && (
            <>
              <div style={{ display: "flex", gap: "20px" }}>
                <RadioCard
                  label="Cargo"
                  selected={configType === "CARGO"}
                  onClick={() => setConfigType("CARGO")}
                />
                <RadioCard
                  label="Região"
                  selected={configType === "REGIAO"}
                  onClick={() => setConfigType("REGIAO")}
                />
                <RadioCard
                  label="Lotação"
                  selected={configType === "LOTACAO"}
                  onClick={() => setConfigType("LOTACAO")}
                />
              </div>
              <TableContainer sx={{
                "& .MuiTableCell-root": { background: "#FFFFFF !important" },
                ...(getSortedItems().length > 15 && { maxHeight: '70vh', overflowY: 'auto' }),
              }}>
                <Table stickyHeader>
                  <TableRow style={{ height: '70px'}}>
                    <TableCell style={{ width: "200px"}}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'rgb(111, 111, 120)', fontWeight: '600' }}>
                        {configType === "CARGO" ? "Cargo" : configType === "REGIAO" ? "Região" : "Lotação"}
                      </span>
                    </TableCell>
                    {selectedWorkspaces.map((workspace, index) => (
                      <TableCell key={workspace.id + index} style={{ width: "300px", textAlign: "center" }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          <Checkbox
                            checked={areAllItemsSelectedForWorkspace(workspace.id)}
                            onChange={() => handleToggleAllItemsForWorkspace(workspace.id)}
                            size="small"
                            title="Selecionar todos"
                          />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'rgb(111, 111, 120)', fontWeight: '600' }}>
                            {formatWorkspaceColumnLabel(workspace)}
                          </span>
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                  {getSortedItems().map((item, itemIndex) => (
                    <TableRow key={item + itemIndex} style={{ height: '70px'}}>
                      <TableCell>{item}</TableCell>
                      {selectedWorkspaces.map((workspace, wsIndex) => (
                        <TableCell key={workspace.id + wsIndex} style={{ textAlign: "center" }}>
                          <Switch
                            checked={isRoleWorkspaceSelected(item, workspace.id)}
                            onChange={() => handleToggleRoleWorkspace(item, workspace.id)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </Table>
              </TableContainer>
            </>
          )}
          {activeStep === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Typography variant="h6" sx={{ color: '#191c1a', fontWeight: 600 }}>
                Selecione os tipos de dependentes permitidos para cada contrato
              </Typography>
              {selectedWorkspaces.map((workspace) => (
                <Box
                  key={workspace.id}
                  sx={{
                    border: '1px solid #E8E8E8',
                    borderRadius: '12px',
                    padding: 3,
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, color: '#191c1a', marginBottom: 2 }}
                  >
                    {formatWorkspaceColumnLabel(workspace)}
                  </Typography>
                  <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid #E8E8E8' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={allowDepOverrideMap[workspace.id] ?? false}
                          onChange={(e) => setAllowDepOverrideMap(prev => ({ ...prev, [workspace.id]: e.target.checked }))}
                        />
                      }
                      label={<span style={{ fontSize: '14px', color: '#191c1a' }}>Permitir que dependentes tenham decisão de optante diferente do titular</span>}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isNoneSelected(workspace.id)}
                          onChange={() => setNenhumForWorkspace(workspace.id)}
                        />
                      }
                      label={<span style={{ fontSize: '16px', color: '#191c1a', fontWeight: 600 }}>{NENHUM_LABEL}</span>}
                    />
                    <Typography variant="caption" sx={{ display: 'block', ml: 4, mb: 1, color: '#5C5C5C' }}>
                      Marque para excluir todos os dependentes deste contrato.
                    </Typography>
                    {DEPENDENT_FAMILY_OPTIONS.map((option) => (
                      <FormControlLabel
                        key={option.family}
                        control={
                          <Checkbox
                            checked={isDependentTypeSelected(workspace.id, option.value)}
                            onChange={() => handleToggleDependentType(workspace.id, option.value)}
                          />
                        }
                        label={<span style={{ fontSize: '16px', color: '#191c1a' }}>{option.label}</span>}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end', gap: "10px","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"24px" }}>
            <Button
              variant='outlined'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={activeStep === 0 ? handleRemoveGroup : handleBack}
            >
              Voltar
            </Button>
            <Button
              variant='contained'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={activeStep === 2 ? handleCreate : handleNext}
              disabled={
                activeStep === 0 ? !selectedWorkspaces.length :
                activeStep === 1 ? !canProceedFromAssignments() :
                false
              }
            >
              {activeStep === 2 ? (isEditMode ? "Salvar Alterações" : "Criar Grupo") : "Próximo"}
            </Button>
          </div>
        </>
      )}
    </>
  )
}
